import {CacheKeyConfig, useGrpcRequest} from '@aboutyou/data-fetcher';
import {useIsSoftFeatureToggleActive} from '@aboutyou/feature-flags';
import {NavigationService_GetHeaderBulkLazy} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/navigation/v1/service.grpc.lazy';
import {
  GetHeaderBulkResponse,
  GetHeaderBulkResponse_GenderSwitchSection,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/navigation/v1/service.types';
import {TargetGroup} from '@aboutyou/grpc-clients/tadarida/aysa_api/shared/v1/target_group.types';
import {useSuspenseSafeHydrationDone} from '@aboutyou/lazy-imports';
import {startTransition, useEffect, useRef, useState} from 'react';
import {useReductionsStateRequestParameter} from '../../../features/ReductionsState/useReductionsStateRequestParameter';
import {useSelectedTargetGroup} from '../../../shared-deprecated/useSelectedTargetGroup';
import {useEntityIdentifier} from './useEntityIdentifier';
import {useHeaderInMemoryCache} from './useHeaderInMemoryCache/useHeaderInMemoryCache';

const useGetHeaderBulkSection = <Section extends keyof GetHeaderBulkResponse>(
  section: Section,
) => {
  const {initialTargetGroup, targetGroup, switchTargetGroup} =
    useSelectedTargetGroup();
  const identifier = useEntityIdentifier();
  const includeFlyoutItems = !useIsSoftFeatureToggleActive(
    'disableDesktopHeaderFlyouts',
  );
  const reductionsState = useReductionsStateRequestParameter();

  const requestParams = {
    sessionTargetGroup: initialTargetGroup ?? null,
    includeFlyoutItems,
    identifier: identifier ?? null,
    reductionsState,
  };

  /**
   * This recommendation from the server render should only be set without breaking the hydration
   */
  const [
    recommendedTargetGroupCallback,
    setRecommendedTargetGroupAfterHydration,
  ] = useState<{
    recommendedTargetGroup: TargetGroup;
    invalidateCache: () => void;
  } | null>(null);
  const initialRecommendedTargetGroupSet = useRef(false);

  useEffect(() => {
    if (
      recommendedTargetGroupCallback &&
      !initialRecommendedTargetGroupSet.current
    ) {
      initialRecommendedTargetGroupSet.current = true;

      switchTargetGroup(recommendedTargetGroupCallback.recommendedTargetGroup);
      recommendedTargetGroupCallback.invalidateCache();
    }
  }, [recommendedTargetGroupCallback, switchTargetGroup]);

  const {validateInMemoryCacheRequest, setHeaderCache} =
    useHeaderInMemoryCache();

  const request = useGrpcRequest(
    NavigationService_GetHeaderBulkLazy,
    requestParams,
    {
      section,
      validateInMemoryCacheRequest,
      onServerSuccess: async (bulkData: GetHeaderBulkResponse) => {
        setHeaderCache(bulkData);
      },
      onRequestInitialDataSuccess: (
        initialData: GetHeaderBulkResponse,
        {invalidateCache, duplicateInitialDataByParams},
      ) => {
        startTransition(() => {
          const recommendedTargetGroup =
            initialData.genderSwitch.recommendedTargetGroup;
          const appliedTargetGroup =
            initialData.genderSwitch.appliedTargetGroup;

          if (section === 'genderSwitch') {
            // The `identifier` is only used for SEO and does not need to ever update on the client. This change makes sure we do not make a request when the identifier changes.
            // The request would still fire when other params like sessionTargetGroup, session changes.
            const {identifier, ...params} = requestParams;

            /**
             *  On the server the sessionTargetGroup is not part of the cache key, as the header view is cached and reused between different requests independent of their page type.
             *  Therefore, we duplicate the initial data with the sessionTargetGroup
             */
            duplicateInitialDataByParams({
              ...params,
              sessionTargetGroup: appliedTargetGroup,
            } as typeof requestParams);

            // If there in no BE recommendedTargetGroup and we dont have a session targetGroup we assume that BE rendered the FEMALE targetGroup to render the header
            // and we use that data create a cache entry for the FEMALE targetGroup
            if (!recommendedTargetGroup && !initialTargetGroup) {
              duplicateInitialDataByParams({
                ...params,
                sessionTargetGroup: appliedTargetGroup,
              } as typeof requestParams);
            }

            if (
              recommendedTargetGroup &&
              recommendedTargetGroup !== initialTargetGroup
            ) {
              // if we made the request with a different sessionTargetGroup (based on the cookie saved on the client) but the BE returned data based on the `identifier` for a different target group,
              // need to update the cache with the same data, but with the new sessionTargetGroup.
              // duplicateInitialDataByParams allows to duplicate the initial data with the new params.
              duplicateInitialDataByParams({
                ...params,
                sessionTargetGroup: recommendedTargetGroup,
              } as typeof requestParams);

              setRecommendedTargetGroupAfterHydration({
                // then switch the targetGroup recommended by the BE based on the `identifier`
                recommendedTargetGroup,
                // we should invalidate the cache for the initial request only after switching the gender(targetGroup), otherwise it will trigger another request with the old targetGroup
                invalidateCache,
              });
            }
          }
        });
      },
      transformCacheKey: useHeaderBulkTransformCacheKey(),
    },
  );

  useEffect(() => {
    if (request.data && !request.isLoading && request.isFetched) {
      // under right conditions request data will be cached under wrong gender
      // here we are checking if that is the case and then refetching it.
      // targetGroup will have right value at this point and would allow for right gender to be fetched
      if (
        section === 'genderSwitch' &&
        (request.data as GetHeaderBulkResponse_GenderSwitchSection)
          .appliedTargetGroup !== targetGroup
      ) {
        request.refetch();
      }
    }
  }, [request, section, targetGroup]);

  return request;
};

const useHeaderBulkTransformCacheKey = () => {
  const includeSessionTargetGroup = useSuspenseSafeHydrationDone();

  return (config: CacheKeyConfig) => {
    // The `identifier` is only used for SEO and does not need to ever update on the client. This change makes sure we do not make a request when the identifier changes.
    // The request would still fire when other params like sessionTargetGroup, session changes.
    const {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      identifier,
      sessionTargetGroup: sessionTargetGroupParam,
      ...params
    } = config.params;

    const sessionTargetGroup = includeSessionTargetGroup
      ? sessionTargetGroupParam
      : undefined;

    return {
      ...config,
      params: {
        ...params,
        sessionTargetGroup,
      },
    };
  };
};

export const useMetaBarSection = () =>
  useGetHeaderBulkSection('metaBarSection');

export const useGenderSwitchSection = () =>
  useGetHeaderBulkSection('genderSwitch');

export const useNavigationBarSection = () => useGetHeaderBulkSection('navBar');
